/**
 * The external-content placeholder embedded in node article HTML.
 *
 * The Map Builder inserts a frame; the Map Viewer finds it by attribute
 * and swaps in the content fetched from the connected system
 * (`maps-backend/derkontext/frontend/js/pages/map/map-init.js`). Writer and
 * readers therefore have to agree on the attribute names exactly — they
 * once drifted apart, and a frame whose provider read as `undefined` was
 * silently re-stamped with whichever provider happened to be listed first.
 *
 * Anything reading or writing a frame goes through here.
 */

export const EXTERNAL_FRAME_CLASS = 'external-frame';
export const EXTERNAL_ID_ATTR = 'data-external-id';
export const EXTERNAL_PROVIDER_ATTR = 'data-external-provider';
// A provider can hold several connections — two Confluence spaces, two
// SharePoint sites — whose item ids come from the same system, so the
// provider alone no longer says where a block's content lives. Both
// server-side resolvers read this: the Map Viewer and the search
// indexer. A frame written before connections existed carries none and
// resolves against the first connection, i.e. the credentials it always
// used, which is why nothing has to rewrite old markup.
export const EXTERNAL_CONNECTION_ATTR = 'data-external-connection';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function attribute(name, value) {
  return value ? ` ${name}="${escapeHtml(value)}"` : '';
}

/**
 * Build the placeholder inserted into the article HTML.
 *
 * @param {object} frame
 * @param {string} frame.id Item id within the connection.
 * @param {string} frame.provider Provider id.
 * @param {string} [frame.connection] Connection id within the provider.
 * @param {string} frame.title Item title, shown while editing.
 * @param {string} [frame.source] Localized line naming the source; falls
 *   back to the provider id for a caller that has no translation at hand.
 * @returns {string} The frame markup.
 */
export function externalFrameHtml({id, provider, connection, title, source}) {
  return (
    `<div contenteditable="false" class="${EXTERNAL_FRAME_CLASS}"` +
    `${attribute(EXTERNAL_ID_ATTR, id)}` +
    `${attribute(EXTERNAL_PROVIDER_ATTR, provider)}` +
    `${attribute(EXTERNAL_CONNECTION_ATTR, connection)}>` +
    `<p class="${EXTERNAL_FRAME_CLASS}__title">${escapeHtml(title)}</p>` +
    `<p class="${EXTERNAL_FRAME_CLASS}__source">` +
    `${escapeHtml(source || `Source: ${provider}`)}</p>` +
    `</div>`
  );
}

/**
 * Whether an element is an external-content placeholder.
 *
 * @param {Element|null} element
 * @returns {boolean}
 */
export function isExternalFrame(element) {
  return Boolean(element?.classList?.contains(EXTERNAL_FRAME_CLASS));
}

/**
 * Read the item, provider and connection a placeholder refers to.
 *
 * @param {Element|null} element
 * @returns {{id: string|null, provider: string|null, connection: string|null}}
 */
export function readExternalFrame(element) {
  return {
    id: element?.getAttribute?.(EXTERNAL_ID_ATTR) ?? null,
    provider: element?.getAttribute?.(EXTERNAL_PROVIDER_ATTR) ?? null,
    connection: element?.getAttribute?.(EXTERNAL_CONNECTION_ATTR) ?? null
  };
}
